import { QueryDataState } from '@shared/interfaces';

import { GiftChoiceUnitData } from '../giftChoiceUnit/giftChoiceUnit.interface';
import { GiftItemForStore } from '../giftItem/giftItem.interface';
import { Image } from '../media/media.interface';

// 공유 상태
export enum GiftCouponShareStatus {
  NotShared = 'NotShared', // 아직 공유 안 함
  Shared = 'Shared', // 공유함 (링크 생성)
  Viewed = 'Viewed', // 받는 사람이 조회함
}

/**
 * 공유 매체 (마지막으로 공유한 채널 / shareHistory에 함께 기록)
 * - KakaoTalk: 카카오톡 (웹훅으로도 자동 기록됨)
 * - SMS: 문자 메시지
 * - Link: 링크 복사 / Web Share API
 * - Other: 그 외
 */
export enum ShareChannel {
  KakaoTalk = 'KakaoTalk',
  SMS = 'SMS',
  Link = 'Link',
  Other = 'Other',
}

/** 기프티콘 발송 매체 (API·관리자 UI·수신 안내 공통) */
export enum DeliveryChannel {
  Officecon = 'Officecon',
  GiftiShow = 'GiftiShow',
  EumGift = 'EumGift',
}

export enum NotificationType {
  AlimTalk = 'AlimTalk',
  SMS = 'SMS',
}

export interface DeliveryChannelInfo {
  label: string;
  notificationType: NotificationType;
  badgeClass: string;
}

export const DELIVERY_CHANNEL_CONFIG: Record<DeliveryChannel, DeliveryChannelInfo> = {
  [DeliveryChannel.Officecon]: {
    label: '오피스콘',
    notificationType: NotificationType.AlimTalk,
    badgeClass: 'border-blue-300 bg-blue-50 text-blue-700',
  },
  [DeliveryChannel.GiftiShow]: {
    label: '기프티쇼',
    notificationType: NotificationType.AlimTalk,
    badgeClass: 'border-red-300 bg-red-50 text-red-700',
  },
  [DeliveryChannel.EumGift]: {
    label: '1566-2373',
    notificationType: NotificationType.SMS,
    badgeClass: 'border-purple-300 bg-purple-50 text-purple-700',
  },
};

/** `DELIVERY_CHANNEL_CONFIG` 키 순서 (추가 enum 값 누락 시 타입 오류) */
export const DELIVERY_CHANNEL_LIST = Object.keys(
  DELIVERY_CHANNEL_CONFIG,
) as DeliveryChannel[];

/** 관리자 발송 매체 `<select>` 표시문구 (config·notificationType만 사용) */
export function formatDeliveryChannelSelectLabel(channel: DeliveryChannel): string {
  const { label, notificationType } = DELIVERY_CHANNEL_CONFIG[channel];
  return notificationType === NotificationType.AlimTalk
    ? `${label} — 알림톡 (카카오톡)`
    : `${label} — 문자 (SMS)`;
}

// 교환 상태
export enum GiftCouponRedeemStatus {
  Available = 'Available', // 교환 가능
  Requested = 'Requested', // 교환 신청됨
  Delivered = 'Delivered', // 기프티콘 전달 완료
  Rejected = 'Rejected', // 발송 불가 (잘못된 번호 등)
  Expired = 'Expired', // 만료됨 (1년 경과)
  Refunded = 'Refunded', // 환불 완료
  Canceled = 'Canceled', // 주문 취소로 인한 폐기
  Disposed = 'Disposed', // 잘못된 전달로 인한 폐기
}

// 쿠폰 기본 인터페이스
export interface GiftCoupon<
  TGiftOrder = string,
  TGiftItem = string,
  TSelectedChoiceUnit = string,
  TGifticonImage = string,
  TDeliveredBy = string,
  TRefundedBy = string,
> {
  _id: string;
  giftOrder: TGiftOrder;
  giftItem: TGiftItem;

  code: string; // 고유 코드 (6자리 hex)
  seq: number; // 주문 내 순번

  // 상태
  shareStatus: GiftCouponShareStatus;
  redeemStatus: GiftCouponRedeemStatus;

  sharedAt?: Date; // 공유한 시각
  viewedAt?: Date; // 받는 사람이 처음 본 시각
  confirmedAt?: Date; // 전달 확인한 시각
  /** 카카오톡 공유 웹훅으로 확인된 실제 전달 시각 */
  kakaoSharedAt?: Date;
  /** 카카오톡 공유 웹훅으로 집계된 서로 다른 카톡방(채팅) 수 */
  shareKakaoCount?: number;
  /** 마지막으로 공유한 매체 (이미 Shared/Viewed 상태에서 channel만 들어와도 갱신) */
  lastSharedChannel?: ShareChannel;
  memo?: string;

  // 선택형 상품인 경우
  selectedChoiceUnit?: TSelectedChoiceUnit;

  // 교환 신청자 정보
  recipient?: {
    name: string;
    phoneNumber: string;
    requestedAt: Date;
    signatureImage?: Image;
  };

  // 관리자가 전달한 기프티콘
  gifticon?: {
    image: TGifticonImage;
    deliveredAt: Date;
    deliveredBy: TDeliveredBy;
    priceWhole: number;
    priceRetail: number;
    priceRebate: number;
    deliveryChannel: DeliveryChannel;
  };

  // 만료 정책
  purchasedAt: Date; // 구매일
  expiresAt: Date; // 구매일 + 1년

  // 환불 처리
  refund?: {
    amount?: number;
    bank?: string;
    accountNumber?: string;
    accountHolder?: string;
    phoneNumber?: string;
    signatureImage?: Image;
    requestedAt?: Date;
    refundedAt?: Date;
    refundedBy?: TRefundedBy;
  };

  // 환불/연장 신청 발행 (관리자가 고객에게 신청 링크를 허용)
  issued?: {
    type: 'refund' | 'renewal';
    issuedAt: Date;
    issuedBy: string;
    requestedAt?: Date;
  };

  shareHistory?: Array<{
    sharedAt: Date;
    unsharedAt?: Date;
    shareChannel?: ShareChannel;
  }>;

  // 재발행 쿠폰 여부
  isAdditional?: boolean;
  // 재발급 쿠폰 여부 (기존 쿠폰 폐기 후 재발급)
  isReissued?: boolean;
  // 연장쿠폰이 발급된 원본 쿠폰
  isRenewed?: boolean;
  // 이 쿠폰 자체가 연장쿠폰
  isRenewal?: boolean;
  // 연장쿠폰의 원본 쿠폰 참조
  originalCoupon?: string;
  // 연장 발급된 쿠폰 (issued 조회 시 populate)
  renewalCoupon?: GiftCoupon;
  // 폐기 사유 (redeemStatus === 'Disposed'일 때)
  disposalReason?: string;
  // 재발행 쿠폰 전달 시 입력한 수신자 전화번호
  additionalRecipientPhone?: string;

  // 번호 인증 기록
  verification?: {
    verificationId: string;
    phoneNumber: string;
    code: string;
    verifiedAt: Date;
  };

  // Virtual
  fullCode: string; // code-seq 형태
  /**
   * 카카오톡 공유 웹훅 역참조 (virtual). 백엔드가 populate 시 채워서 내려준다.
   * 같은 쿠폰이 여러 채팅방에 공유되었을 경우 콜백이 여러 건 누적된다.
   */
  kakaoShareCallbacks?: KakaoShareCallback[];

  createdAt: Date;
  updatedAt: Date;
}

// Store에서 사용하는 타입 (쿠폰 공유 페이지용)
export type GiftCouponForStore = GiftCoupon<
  string, // giftOrder ID
  GiftItemForStore, // giftItem populate
  GiftChoiceUnitData, // selectedChoiceUnit populate
  Image, // gifticon image
  string, // deliveredBy
  string // refundedBy
>;

export type GiftCouponForStoreState = QueryDataState<GiftCouponForStore>;

export type GiftCouponListForStore = GiftCouponForStore[];

export type GiftCouponListForStoreState = QueryDataState<GiftCouponListForStore>;

// Public에서 사용하는 타입 (쿠폰 교환 페이지용)
export type GiftCouponForPublic = GiftCoupon<
  {
    _id: string;
    receiverName?: string;
  }, // giftOrder 일부 populate
  GiftItemForStore, // giftItem populate
  GiftChoiceUnitData, // selectedChoiceUnit populate
  Image, // gifticon image
  string, // deliveredBy
  string // refundedBy
>;

export type GiftCouponForPublicState = QueryDataState<GiftCouponForPublic>;

// Admin 교환신청 목록에서 사용하는 타입 (giftOrder populate 포함)
export interface GiftCouponOrderInfo {
  _id: string;
  paymentDetail?: {
    purchasedAt?: Date;
  };
  bugo?: {
    _id: string;
    member?: {
      _id: string;
      fullName: string;
      phoneNumber: string;
    };
  };
}

export type GiftCouponForRedemption = GiftCoupon<
  GiftCouponOrderInfo, // giftOrder populate
  GiftItemForStore, // giftItem populate
  GiftChoiceUnitData, // selectedChoiceUnit populate
  Image, // gifticon image
  string, // deliveredBy
  string // refundedBy
>;

// Admin 주문목록에서 사용하는 간소화 쿠폰 타입 (virtual populate select)
export interface GiftCouponSummary {
  _id: string;
  giftItem: string;
  code: string;
  seq: number;
  shareStatus: GiftCouponShareStatus;
  redeemStatus: GiftCouponRedeemStatus;
  sharedAt?: Date;
  viewedAt?: Date;
  confirmedAt?: Date;
  /** 카카오톡 공유 웹훅으로 확인된 실제 전달 시각 */
  kakaoSharedAt?: Date;
  /** 카카오톡 공유 웹훅으로 집계된 서로 다른 카톡방(채팅) 수 */
  shareKakaoCount?: number;
  /** 마지막으로 공유한 매체 */
  lastSharedChannel?: ShareChannel;
  memo?: string;
  selectedChoiceUnit?: GiftChoiceUnitData | string;
  recipient?: {
    name: string;
    phoneNumber: string;
    requestedAt: Date;
    signatureImage: Image;
  };
  gifticon?: {
    deliveredAt?: Date;
  };
  purchasedAt: Date;
  expiresAt: Date;
  refund?: {
    amount?: number;
    bank?: string;
    accountNumber?: string;
    accountHolder?: string;
    phoneNumber?: string;
    refundedAt?: Date;
    requestedAt?: Date;
  };
  issued?: {
    type: 'refund' | 'renewal';
    issuedAt: Date;
    issuedBy: string;
    requestedAt?: Date;
  };
  shareHistory?: Array<{
    sharedAt: Date;
    unsharedAt?: Date;
    shareChannel?: ShareChannel;
  }>;
  isAdditional?: boolean;
  isReissued?: boolean;
  isRenewed?: boolean;
  isRenewal?: boolean;
  originalCoupon?: string;
  disposalReason?: string;
  additionalRecipientPhone?: string;
  renewalRecipientPhone?: string;
  /**
   * 카카오톡 공유 웹훅 역참조 (virtual). 백엔드가 populate 시 채워서 내려준다.
   * 같은 쿠폰이 여러 채팅방에 공유되었을 경우 콜백이 여러 건 누적된다.
   */
  kakaoShareCallbacks?: KakaoShareCallback[];
}

// 교환 신청 DTO
export interface GiftCouponRequestDto {
  name: string; // 교환 신청자 이름
  phoneNumber: string; // 교환 신청자 전화번호
  signatureImage?: string; // Base64 Data URL (data:image/png;base64,...)
  selectedChoiceUnit?: string; // 선택형 상품인 경우 선택한 ChoiceUnit ID
}

// 나중에 신청하기 — 수신자 번호만 미리 저장
export interface GiftCouponSaveRecipientDto {
  phoneNumber: string;
  selectedChoiceUnit?: string;
}

// 블랙리스트 항목 (중복 의심 번호)
export interface BlacklistItem {
  phoneNumber: string;
  count: number;
  names: string[];
}

// 쿠폰 통계 (Admin statistics)
export interface GiftCouponStatistics {
  pending: number;
  delivered: number;
  rejected: number;
  canceled: number;
  disposed: number;
  total: number;
  blacklist: BlacklistItem[];
}

// 과거 교환 이력 항목
export interface PhoneHistoryItem {
  redeemStatus: GiftCouponRedeemStatus;
  requestedAt: Date;
  ordererName: string;
  giftItemName?: string;
  selectedChoiceUnit?: {
    name: string;
    image?: Image | string;
  };
}

// 주문 단위 쿠폰 요약 (발송대기 대시보드 API 응답)
export interface OrderCouponSummary {
  purchasedCount: number;
  totalCouponCount: number;
  additionalCount: number;
  deliveredCount: number;
  requestedCount: number;
}

// 발송대기 대시보드용 쿠폰 (isDuplicatePhone, phoneHistory 포함)
export type GiftCouponForRedemptionDashboard = GiftCouponForRedemption & {
  isDuplicatePhone?: boolean;
  isAdditionalPhoneConflict?: boolean;
  phoneHistory?: PhoneHistoryItem[];
  redeemSequence?: number;
  orderCouponSummary?: OrderCouponSummary;
};

// 발송대기 대시보드 응답
export interface RedemptionDashboardData {
  requested: GiftCouponForRedemptionDashboard[];
  todayDelivered: GiftCouponForRedemption[];
}

// 쿠폰 검색 파라미터
export type DateField = 'requestedAt' | 'deliveredAt';

export interface GiftCouponSearchParams {
  phoneNumber?: string;
  ordererName?: string;
  ordererPhone?: string;
  fullCode?: string;
  dateFrom?: string;
  dateTo?: string;
  dateField?: DateField;
  redeemStatus?: GiftCouponRedeemStatus | GiftCouponRedeemStatus[];
  shareStatus?: GiftCouponShareStatus;
}

// 코드로 쿠폰 조회 시 사용하는 타입
export type GiftCouponByCode = GiftCouponForPublic;

export type GiftCouponByCodeState = QueryDataState<GiftCouponByCode>;

// 카카오톡 공유 웹훅 콜백
// args.shareContent === 'GiftCoupon' | 'GiftDelivery' 로 구분되며
// 각각 args.couponId / args.deliveryId, args.couponCode, args.domainUrl 를 포함합니다.
export interface KakaoShareCallback {
  _id: string;
  chatType: string;
  hashChatId: string;
  templateId: number;
  args: {
    shareContent?: 'GiftCoupon' | 'GiftDelivery';
    couponId?: string;
    deliveryId?: string;
    couponCode?: string;
    domainUrl?: string;
    [key: string]: string | undefined;
  };
  /** 백엔드에서 args.couponId 를 별도 필드로 노출 (조건부) */
  couponId?: string;
  /** 백엔드에서 args.deliveryId 를 별도 필드로 노출 (조건부) */
  deliveryId?: string;
  appName?: string;
  kakaoResourceId?: string;
  createdAt: string;
  updatedAt: string;
}

// 번호 인증 관련 DTO / 응답
export interface VerificationSendDto {
  phoneNumber: string;
  giftCouponId: string;
  reason?: string;
}

export interface VerificationSendResponse {
  verificationId: string;
  expiresAt: string;
}

export interface VerificationConfirmDto {
  verificationId: string;
  code: string;
  giftCouponId: string;
  phoneNumber: string;
}

export interface VerificationConfirmResponse {
  verified: boolean;
}
